import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/Spinner';
import { LenderAutocomplete } from '@/components/LenderAutocomplete';
import type { CplFormValues } from '@/types/api';

const schema = z.object({
  closingDate: z.string().min(1, 'Closing date is required'),
  fileNumber: z.string().min(1, 'File number is required'),
  sellerName: z.string(),
  formTypeId: z.coerce.number(),
  buyerName: z.string(),
  propertyAddress: z.string(),
  propertyCity: z.string(),
  propertyCounty: z.string(),
  propertyState: z.string(),
  propertyZip: z.string(),
  companyName: z.string(),
  companyAddress: z.string(),
  companyCity: z.string(),
  companyState: z.string(),
  companyZip: z.string(),
  referenceNumber: z.string(),
  isaoa: z.boolean(),
});
type Values = z.infer<typeof schema>;

const EMPTY: Values = {
  closingDate: '', fileNumber: '', sellerName: '', formTypeId: 1, buyerName: '',
  propertyAddress: '', propertyCity: '', propertyCounty: 'Oklahoma', propertyState: 'OK',
  propertyZip: '', companyName: '', companyAddress: '', companyCity: '', companyState: 'OK',
  companyZip: '', referenceNumber: '', isaoa: false,
};

export default function CplFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const id = params.id ? Number(params.id) : null;
  const isEdit = id !== null;

  const { data: formTypes = [] } = useQuery({ queryKey: ['cpl-form-types'], queryFn: () => api.getCplFormTypes() });
  const { data: counties = [] } = useQuery({ queryKey: ['cpl-counties'], queryFn: () => api.getCplCounties() });
  const { data: states = [] } = useQuery({ queryKey: ['cpl-states'], queryFn: () => api.getCplStates() });
  const { data: agent } = useQuery({ queryKey: ['cpl-agent'], queryFn: () => api.getCplAgent() });
  // Editing → show the CPL creator's agent (from the record); new → the current user's.
  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['cpl', id],
    queryFn: () => api.getCpl(id as number),
    enabled: isEdit,
  });
  const displayAgent = isEdit ? existing?.agent : agent;

  const {
    register, handleSubmit, reset, control, setValue, getValues,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  // Populate the form once the record loads (edit mode).
  useEffect(() => {
    if (existing) {
      const { id: _id, void: _v, agent: _a, ...values } = existing;
      void _id; void _v; void _a;
      reset(values);
    }
  }, [existing, reset]);

  // Select options load async; once present, re-push the current values to the
  // DOM so the displayed option matches the form state (otherwise an
  // uncontrolled <select> shows the first option while the form holds the
  // default/record value — e.g. shows "Adair" but holds "Oklahoma"). Runs once.
  const optionsSynced = useRef(false);
  useEffect(() => {
    if (optionsSynced.current) return;
    if (formTypes.length === 0 || counties.length === 0 || states.length === 0) return;
    optionsSynced.current = true;
    const v = getValues();
    setValue('formTypeId', v.formTypeId);
    setValue('propertyCounty', v.propertyCounty);
    setValue('companyState', v.companyState);
  }, [formTypes, counties, states, getValues, setValue]);

  const onSubmit = async (values: Values) => {
    const payload = values as CplFormValues;
    if (isEdit) await api.updateCpl(id as number, payload);
    else await api.createCpl(payload);
    // Force the list (and this record) to refetch so the edit shows without a
    // manual refresh (the list query has a 30s staleTime).
    await queryClient.invalidateQueries({ queryKey: ['cpls'] });
    if (isEdit) queryClient.invalidateQueries({ queryKey: ['cpl', id] });
    // Return to where they came from (the list, on the same page/filters).
    navigate(-1);
  };

  if (isEdit && loadingExisting) {
    return <div className="ast-cpl text-center py-5"><Spinner /></div>;
  }

  return (
    <div className="ast-cpl ast-cpl-form">
      <div className="ast-page-head">
        <h2 className="serif ast-page-title">{isEdit ? 'Edit CPL' : 'New CPL'}</h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* File Details */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">File Details</h3>
          <div className="ast-grid-2">
            <FormField label="Closing Date" htmlFor="closing_date" error={errors.closingDate?.message}>
              <input id="closing_date" type="date" className="form-control form-control-sm" {...register('closingDate')} />
            </FormField>
            <FormField label="File Number" htmlFor="file_number" error={errors.fileNumber?.message}>
              <input id="file_number" className="form-control form-control-sm" {...register('fileNumber')} />
            </FormField>
            <FormField label="Seller Name" htmlFor="seller_name">
              <input id="seller_name" className="form-control form-control-sm" {...register('sellerName')} />
            </FormField>
            <FormField label="Form Selection" htmlFor="form_type">
              <select id="form_type" className="form-select form-select-sm" {...register('formTypeId')}>
                {formTypes.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </FormField>
          </div>
        </section>

        {/* Buyer / Borrower */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Buyer / Borrower Information</h3>
          <div className="ast-grid-2">
            <FormField label="Name" htmlFor="buyer_name">
              <input id="buyer_name" className="form-control form-control-sm" {...register('buyerName')} />
            </FormField>
            <FormField label="Property Address" htmlFor="prop_address">
              <input id="prop_address" className="form-control form-control-sm" {...register('propertyAddress')} />
            </FormField>
            <FormField label="Property City" htmlFor="prop_city">
              <input id="prop_city" className="form-control form-control-sm" {...register('propertyCity')} />
            </FormField>
            <FormField label="Property County" htmlFor="prop_county">
              <select id="prop_county" className="form-select form-select-sm" {...register('propertyCounty')}>
                {counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label="Property State" htmlFor="prop_state">
              {/* Properties are always in Oklahoma — fixed, not editable (matches the jacket form). */}
              <input id="prop_state" className="form-control form-control-sm" readOnly {...register('propertyState')} />
            </FormField>
            <FormField label="Property Zip Code" htmlFor="prop_zip">
              <input id="prop_zip" className="form-control form-control-sm" maxLength={10} {...register('propertyZip')} />
            </FormField>
          </div>
        </section>

        {/* Lender */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Lender Information</h3>
          <div className="ast-grid-2">
            <FormField label="Name" htmlFor="company_name">
              <Controller name="companyName" control={control}
                render={({ field }) => (
                  <LenderAutocomplete
                    id="company_name"
                    value={field.value}
                    onChange={field.onChange}
                    onSelect={(s) => {
                      setValue('companyAddress', s.add);
                      setValue('companyCity', s.city);
                      if (s.state) setValue('companyState', s.state);
                      setValue('companyZip', s.zip);
                    }}
                  />
                )} />
            </FormField>
            <FormField label="Address" htmlFor="company_address">
              <input id="company_address" className="form-control form-control-sm" {...register('companyAddress')} />
            </FormField>
            <FormField label="City" htmlFor="company_city">
              <input id="company_city" className="form-control form-control-sm" {...register('companyCity')} />
            </FormField>
            <FormField label="State" htmlFor="company_state">
              <select id="company_state" className="form-select form-select-sm" {...register('companyState')}>
                {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="Zip Code" htmlFor="company_zip">
              <input id="company_zip" className="form-control form-control-sm" maxLength={10} {...register('companyZip')} />
            </FormField>
            <FormField label="Loan Number" htmlFor="reference_number">
              <input id="reference_number" className="form-control form-control-sm" {...register('referenceNumber')} />
            </FormField>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="checkbox" id="isaoa" {...register('isaoa')} />
            <label className="form-check-label" htmlFor="isaoa">ISAOA</label>
          </div>
        </section>

        {/* Agent (read-only) */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Agent Information</h3>
          <div className="ast-agent-block sans-serif">
            <div>{displayAgent?.name ?? '—'}</div>
            <div>{displayAgent?.agency ?? ''}</div>
            <div>{displayAgent?.address ?? ''}</div>
            <div>{displayAgent ? `${displayAgent.city}, ${displayAgent.state} ${displayAgent.zip}` : ''}</div>
          </div>
        </section>

        {/* Actions */}
        <div className="ast-form-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
            Back
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => reset(isEdit && existing
            ? (() => { const { id: _i, void: _v, agent: _a, ...v } = existing; void _i; void _v; void _a; return v; })()
            : EMPTY)}>
            Clear
          </button>
          <button type="submit" className="btn btn-ast-blue" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Update' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}
